import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHead } from "@/pages/admin/AdminLayout";
import { LocationPicker } from "@/components/LocationPicker";
import { StopsEditor } from "@/components/booking/StopsEditor";
import { BookingTypePicker, TripOptions, TypeOptions } from "@/components/booking/TripOptions";
import { CheckoutPanel } from "@/components/booking/CheckoutPanel";
import { ScheduleNotice } from "@/components/booking/ScheduleNotice";
import { DEFAULT_OPTS as BASE_OPTS, buildTrip, isMultiStop, useQuote } from "@/components/booking/useQuote";
import { QuoteCard, QuoteProblem } from "@/components/QuoteCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BookingList } from "@/pages/admin/BookingList";
import { BOOKING_TYPES } from "@/lib/rides";

const pastMin = () => { const d = new Date(); d.setDate(d.getDate() - 10); d.setHours(0, 0, 0, 0); return toLocal(d.toISOString()); };

function PastFields({ opts, setOpts, fare, setFare, quote }) {
  return (
    <div className="space-y-3 rounded-xl border border-amber-400/40 bg-amber-400/5 p-3" data-testid="past-ride-fields">
      <p className="text-xs text-amber-100">Recording a ride that already happened (up to 10 days back). The rider must approve it before it counts. No charge, credit or payment is created.</p>
      <label className="block text-xs font-semibold">Date and time of the ride
        <Input type="datetime-local" min={pastMin()} max={toLocal(new Date().toISOString())} value={opts.scheduledAt}
          onChange={(e) => setOpts({ ...opts, when: "later", scheduledAt: e.target.value })} data-testid="past-ride-datetime" /></label>
      <label className="block text-xs font-semibold">Fare to record (optional, defaults to the calculated fare{quote?.final_fare != null ? ` $${quote.final_fare.toFixed(2)}` : ""})
        <Input type="number" min="0" step="0.01" value={fare} onChange={(e) => setFare(e.target.value)} data-testid="past-ride-fare" /></label>
      {opts.bookingType === "recurring" && <p className="text-xs text-destructive">Past rides are recorded one at a time. Pick One-Way, Round Trip or Multi-Stop.</p>}
    </div>
  );
}

const DEFAULT_OPTS = { ...BASE_OPTS, when: "later" };
const tripTypeOf = (r) => r.trip_type || (r.stops?.length ? "multi_stop" : "one_way");
const toLocal = (iso) => { const d = new Date(iso); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };

function RiderPicker({ rider, setRider }) {
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const { data } = useQuery({ queryKey: ["riderSearch", q], queryFn: () => api.get("/admin/users", { params: { q } }).then((r) => r.data), enabled: q.length >= 2 });
  const create = async () => {
    try {
      const { data: res } = await api.post("/admin/users", form);
      toast(res.existing ? "Existing customer found — using that account" : "Customer created");
      setRider(res.user);
      setForm(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  if (rider) {
    return (
      <div className="flex items-center justify-between rounded-xl border bg-card p-3" data-testid="quickbook-selected-rider">
        <div><p className="font-semibold">{rider.name}</p><p className="text-xs text-muted-foreground">{rider.email}{rider.phone ? ` · ${rider.phone}` : ""}</p></div>
        <Button size="sm" variant="ghost" onClick={() => setRider(null)} data-testid="quickbook-change-rider">Change</Button>
      </div>
    );
  }
  return (
    <div className="space-y-2 rounded-xl border bg-card p-3">
      <Input placeholder="Search rider by name, email or phone" value={q} onChange={(e) => setQ(e.target.value)} data-testid="quickbook-rider-search" />
      {data?.map((u) => (
        <button key={u.id} type="button" onClick={() => setRider(u)} className="block w-full rounded-lg px-2 py-1.5 text-left text-sm hover:bg-muted" data-testid="quickbook-rider-result">
          {u.name} <span className="text-xs text-muted-foreground">· {u.email}</span>
        </button>
      ))}
      {form ? (
        <div className="grid gap-2 sm:grid-cols-3">
          {["name", "email", "phone"].map((k) => <Input key={k} placeholder={k} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`new-customer-${k}`} />)}
          <Button onClick={create} disabled={!form.name || !form.email} data-testid="new-customer-save">Create customer</Button>
        </div>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => setForm({ name: "", email: "", phone: "" })} data-testid="quickbook-create-customer">+ Create customer</Button>
      )}
    </div>
  );
}

export default function QuickBook() {
  const { rideId } = useParams();
  const [params] = useSearchParams();
  const [past, setPast] = useState(params.get("mode") === "past");
  const [cart, setCart] = useState([]);
  const [cartBusy, setCartBusy] = useState(false);
  const [fare, setFare] = useState("");
  const navigate = useNavigate();
  const [rider, setRider] = useState(null);
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [stops, setStops] = useState([]);
  const [opts, setOpts] = useState(DEFAULT_OPTS);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("confirmed");
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState("");
  const [schedWarn, setSchedWarn] = useState("");
  const trip = buildTrip({ pickup, dropoff, stops, opts });
  const quote = useQuote(trip, rider?.id, past ? "/admin/rides/historical/quote" : "/pricing/quote");
  const tripKey = JSON.stringify(trip);
  useEffect(() => {
    setSchedWarn("");
    setDup("");
  }, [tripKey, status]);
  const q = trip ? quote.data : null;
  const { data: config } = useQuery({ queryKey: ["pricingConfig"], queryFn: () => api.get("/pricing/config").then((r) => r.data) });

  useEffect(() => {
    if (!rideId) return;
    api.get(`/admin/rides/${rideId}`).then(({ data: { ride } }) => {
      setRider({ id: ride.rider_id, name: ride.rider_name, email: "", phone: ride.rider_phone });
      setPickup(ride.pickup); setDropoff(ride.dropoff); setNotes(ride.notes || "");
      setStops(ride.stops.map((s, i) => ({ key: i, place: s, wait: s.wait_minutes })));
      setOpts({ ...DEFAULT_OPTS, vehicle: ride.vehicle_class, passengers: ride.passengers, bookingType: tripTypeOf(ride),
        returnWait: ride.return_wait_minutes || 0, when: "later", scheduledAt: toLocal(ride.scheduled_at) });
    });
  }, [rideId]);

  const resetTrip = () => { setPickup(null); setDropoff(null); setStops([]); setNotes(""); setFare(""); setOpts({ ...DEFAULT_OPTS, bookingType: opts.bookingType }); };
  const pickRider = (r) => { setRider(r); setCart([]); };
  const patchItem = (key, p) => setCart((c) => c.map((x) => (x.key === key ? { ...x, ...p } : x)));
  const addToList = () => {
    const n = q.occurrences?.length || 1;
    const total = q.occurrence_fares ? q.occurrence_fares.reduce((a, b) => a + b, 0) : q.final_fare;
    setCart((c) => [...c, { key: `${Date.now()}`, status: "ready", body: { ...trip, rider_id: rider.id, status, notes: notes || null },
      route: `${pickup.name} → ${dropoff.name}`, when: trip.scheduled_at, type: BOOKING_TYPES[opts.bookingType] || opts.bookingType, rides: n, total }]);
    toast.success("Added to the booking list. Enter the next ride.");
    resetTrip();
  };
  const createItem = async (item, override = false) => {
    patchItem(item.key, { status: "creating", error: "" });
    try {
      await api.post("/admin/rides", { ...item.body, allow_duplicate: override, allow_schedule_override: override });
      patchItem(item.key, { status: "created" });
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      patchItem(item.key, { status: "failed", error: formatApiError(e), canOverride: ["duplicate", "schedule_warning"].includes(code) });
    }
  };
  const submitList = async () => {
    setCartBusy(true);
    for (const item of cart.filter((x) => x.status !== "created")) await createItem(item); // eslint-disable-line no-await-in-loop
    setCartBusy(false);
  };
  const savePast = async () => {
    setBusy(true);
    try {
      await api.post("/admin/rides/historical", { ...trip, recurrence: null, rider_id: rider.id, notes: notes || null, fare_override: fare === "" ? null : Number(fare) });
      toast.success("Past ride recorded. Waiting for the rider's approval.");
      resetTrip();
    } catch (e) {
      toast.error(formatApiError(e), { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  const save = async (allowDuplicate = false, allowOverride = false) => {
    setBusy(true);
    try {
      if (rideId) {
        await api.patch(`/admin/rides/${rideId}`, { ...trip, recurrence: null, notes: notes || null });
        toast.success("Request updated — fare recalculated");
      } else {
        const { data } = await api.post("/admin/rides", { ...trip, rider_id: rider.id, status, notes: notes || null, allow_duplicate: allowDuplicate, allow_schedule_override: allowOverride });
        toast.success(`${data.rides.length} ride${data.rides.length > 1 ? "s" : ""} ${status === "confirmed" ? "confirmed" : "saved as pending"}`);
      }
      navigate("/admin");
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      if (code === "duplicate") setDup(formatApiError(e));
      else if (code === "schedule_warning") setSchedWarn(formatApiError(e));
      else toast.error(formatApiError(e), { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fade-up grid gap-6 xl:grid-cols-[1fr_420px]" data-testid="quick-book-page">
      <div className="space-y-4">
        <PageHead title={rideId ? "Edit request" : "Manage Bookings"} sub="Same Scoopz pricing engine as riders. Add several rides to one booking list, or record a past ride." />
        {!rideId && (
          <div className="flex gap-2" data-testid="booking-mode">
            {[[false, "New rides"], [true, "Record a past ride"]].map(([v, l]) => (
              <button key={l} type="button" onClick={() => { setPast(v); setOpts({ ...opts, when: "later", scheduledAt: "" }); }} data-testid={`booking-mode-${v ? "past" : "new"}`}
                className={`flex-1 rounded-full border px-3 py-2 text-sm font-semibold ${past === v ? "border-primary bg-primary text-primary-foreground" : "border-white/15 text-muted-foreground"}`}>{l}</button>
            ))}
          </div>
        )}
        {!rideId && <RiderPicker rider={rider} setRider={pickRider} />}
        {!rideId && <BookingTypePicker value={opts.bookingType} onChange={(bookingType) => setOpts({ ...opts, bookingType })} />}
        <LocationPicker kind="pickup" label="Pickup" value={pickup} onChange={setPickup} />
        {isMultiStop(opts) && <StopsEditor stops={stops} setStops={setStops} tiers={config?.wait_tiers} />}
        <LocationPicker kind="dropoff" label="Destination" value={dropoff} onChange={setDropoff} />
        {past ? <PastFields opts={opts} setOpts={setOpts} fare={fare} setFare={setFare} quote={q} /> : <TripOptions opts={opts} setOpts={setOpts} config={config} allowNow />}
        <TypeOptions opts={opts} setOpts={setOpts} config={config} />
        <Textarea placeholder="Ride notes / special instructions" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="quickbook-notes" />
      </div>
      <div className="space-y-4 xl:sticky xl:top-6 xl:self-start">
        {quote.loading && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Calculating fare…</p>}
        {quote.error && <p className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="quote-error">{quote.error}</p>}
        {q?.status === "ok" && <QuoteCard quote={q} pickup={pickup} dropoff={dropoff} />}
        {q?.status === "ok" && !rideId && rider && !past && <CheckoutPanel quote={q} opts={opts} setOpts={setOpts} />}
        {q && q.status !== "ok" && <QuoteProblem quote={q} onRetry={quote.retry} />}
        {past && (
          <Button className="h-12 w-full rounded-full text-base" disabled={busy || !rider || !trip || opts.bookingType === "recurring" || (q?.status !== "ok" && fare === "")} onClick={savePast} data-testid="past-ride-submit">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit for rider approval"}
          </Button>
        )}
        {!past && <>
        {!rideId && (
          <div className="flex gap-2">
            {["confirmed", "pending"].map((s) => (
              <button key={s} type="button" onClick={() => setStatus(s)} data-testid={`quickbook-status-${s}`}
                className={`flex-1 rounded-full border px-3 py-2 text-sm font-semibold capitalize ${status === s ? "border-primary bg-primary text-primary-foreground" : "border-white/15 text-muted-foreground"}`}>Save as {s}</button>
            ))}
          </div>
        )}
        {dup && (
          <div className="space-y-2 rounded-xl border border-amber-400/40 bg-amber-400/10 p-3 text-sm text-amber-100" data-testid="duplicate-warning">
            <p>{dup}</p>
            <Button size="sm" variant="outline" onClick={() => save(true, !!schedWarn)} data-testid="create-anyway-button">Create anyway</Button>
          </div>
        )}
        {q && !rideId && <ScheduleNotice schedule={q.schedule} setOpts={setOpts} admin />}
        {schedWarn && (
          <div className="space-y-2 rounded-xl border border-amber-400/40 bg-amber-400/10 p-3 text-sm text-amber-100" data-testid="schedule-override-warning">
            <p>WARNING: {schedWarn}</p>
            <Button size="sm" variant="outline" onClick={() => save(!!dup, true)} data-testid="book-anyway-button">Book anyway (override)</Button>
          </div>
        )}
        <Button className="h-12 w-full rounded-full text-base" disabled={busy || q?.status !== "ok" || (!rideId && !rider)} onClick={() => save(false)} data-testid="quickbook-confirm-button">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : rideId ? "Save changes" : q?.recurring?.weeks ? `Confirm ${q.occurrences.length} rides · $${q.recurring.series_total.toFixed(2)}` : `Confirm ride${q?.final_fare != null ? ` · $${q.final_fare.toFixed(2)}` : ""}`}
        </Button>
        {!rideId && (
          <Button variant="outline" className="h-11 w-full rounded-full" disabled={q?.status !== "ok" || !rider || cartBusy} onClick={addToList} data-testid="add-to-booking-list">+ Add to booking list (book several rides)</Button>
        )}
        <BookingList items={cart} busy={cartBusy} onSubmit={submitList} onRemove={(k) => setCart((c) => c.filter((x) => x.key !== k))}
          onRetry={(k) => createItem(cart.find((x) => x.key === k), true)} />
        </>}
      </div>
    </div>
  );
}
